import { useEffect, useState, useCallback } from "react";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Globe, RefreshCw, ExternalLink } from "lucide-react";
import { toast } from "sonner";

/**
 * Global Solo Roster — every solo user routed into the "ArmIQ House" tenant.
 * Admin can override their daily hybrid (A–E) inline; deep-link into the Coach
 * Console to operate on them exactly as a team coach would.
 */
const HYBRIDS = ["A", "B", "C", "D", "E"];

export default function GlobalSoloRosterCard() {
  const [data, setData] = useState({ items: [], total: 0 });
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get("/admin/solo-roster");
      setData(r.data);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const migrate = async () => {
    try {
      const r = await api.post("/admin/migrate-solo-users");
      toast.success(`Migrated ${r.data.migrated} solo user(s)`);
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const overrideHybrid = async (uid, hybrid) => {
    try {
      await api.post(`/admin/solo-users/${uid}/override-hybrid`, { hybrid });
      toast.success(`Pinned ${hybrid} for today`);
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const items = data.items || [];

  return (
    <section className="rounded-xl ink-card overflow-hidden" data-testid="admin-solo-roster">
      <div className="px-5 py-4 border-b border-[var(--ink-border)] flex items-center justify-between flex-wrap gap-3">
        <div className="flex items-center gap-2">
          <Globe className="h-4 w-4 text-white/60" />
          <h2 className="font-display font-bold text-lg">Global Solo Roster</h2>
          <span className="text-[10px] tracking-[0.2em] uppercase font-bold text-white/40 ml-2">ArmIQ House</span>
        </div>
        <div className="flex items-center gap-2">
          <div className="text-right">
            <div className="text-[10px] tracking-[0.2em] uppercase font-bold text-white/40">Total</div>
            <div className="font-display font-black text-2xl font-mono-num text-white" data-testid="solo-roster-total">{data.total}</div>
          </div>
          <Button variant="outline" size="sm" onClick={migrate} className="border-white/15 text-white bg-white/5 hover:bg-white/10" data-testid="solo-migrate-btn">
            Migrate orphans
          </Button>
          <Button variant="outline" size="sm" onClick={load} className="border-white/15 text-white bg-white/5 hover:bg-white/10" data-testid="solo-refresh-btn">
            <RefreshCw className="h-3.5 w-3.5" />
          </Button>
        </div>
      </div>

      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="text-[10px] tracking-[0.25em] font-bold text-white/40 uppercase">
            <tr className="border-b border-[var(--ink-border)]">
              <th className="text-left px-5 py-3">Player</th>
              <th className="text-left px-5 py-3">Plan</th>
              <th className="text-right px-5 py-3">Readiness</th>
              <th className="text-right px-5 py-3">Last log</th>
              <th className="text-center px-5 py-3">Pin hybrid (today)</th>
              <th className="text-right px-5 py-3">Coach</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td className="px-5 py-6 text-white/40" colSpan={6}>Loading…</td></tr>
            ) : items.length === 0 ? (
              <tr><td className="px-5 py-6 text-white/40" colSpan={6}>No solo users yet. Click <em>Migrate orphans</em> to backfill.</td></tr>
            ) : items.map((u) => (
              <tr key={u.user_id} className="border-b border-[var(--ink-border)] hover:bg-white/[0.025]" data-testid={`solo-row-${u.user_id}`}>
                <td className="px-5 py-3">
                  <div className="text-white text-sm">{u.name || "—"}</div>
                  <div className="text-[11px] text-white/40 font-mono-num">{u.email}</div>
                </td>
                <td className="px-5 py-3 text-white/70">{u.plan || "free"}</td>
                <td className="px-5 py-3 text-right">
                  <span className="font-mono-num text-white font-bold">{u.last_readiness ?? "—"}</span>
                </td>
                <td className="px-5 py-3 text-right text-white/60 text-xs font-mono-num">{u.last_log_date ?? "—"}</td>
                <td className="px-5 py-3">
                  <div className="flex items-center justify-center gap-1">
                    {HYBRIDS.map((h) => (
                      <button
                        key={h}
                        onClick={() => overrideHybrid(u.user_id, h)}
                        data-testid={`solo-pin-${u.user_id}-${h}`}
                        className={`w-7 h-7 rounded-md font-bold text-xs transition ${u.last_hybrid === h ? "bg-cyan-500 text-white" : "bg-white/5 text-white/60 hover:bg-white/10"}`}
                      >
                        {h}
                      </button>
                    ))}
                  </div>
                </td>
                <td className="px-5 py-3 text-right">
                  <a href={`/coach/player/${u.user_id}?as_admin=1`} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 text-xs text-cyan-300 hover:text-cyan-200" data-testid={`solo-view-${u.user_id}`}>
                    Open <ExternalLink className="h-3 w-3" />
                  </a>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </section>
  );
}
